import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Button, IconButton, CircularProgress, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControlLabel, Switch, Divider, Link
} from '@mui/material';
import { Home, Add, Edit, Delete, Business, Phone, Email, AccountBalance, Receipt, WarningAmber, RequestQuote, ShoppingCart, Description, ReceiptLong } from '@mui/icons-material';
import axios from 'axios';
import Toast from './Toast';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import { fmt, sym } from '../utils/currency';
import { useGLAccounts } from '../hooks/useGLAccounts';
import { glAccountOptions } from '../utils/glAccountOptions';

const emptyForm = {
  supplierCode: '', supplierName: '', contactPerson: '', phone: '',
  email: '', address: '', taxNo: '', bankName: '', bankAccountNo: '',
  bankAccountName: '', apGLAccount: '', isActive: true
};

const InfoRow = ({ label, value }) => (
  <Box display="flex" gap={1} mb={0.5}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 500 }}>{label}</Typography>
    <Typography variant="body2">{value || <em style={{ color: '#bbb' }}>—</em>}</Typography>
  </Box>
);

const SupplierList = () => {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [balances,  setBalances]  = useState({});
  const [error,     setError]     = useState('');
  const [loading,   setLoading]   = useState(true);

  // Edit dialog
  const [editOpen,     setEditOpen]     = useState(false);
  const [editingCode,  setEditingCode]  = useState(null);
  const [form,         setForm]         = useState(emptyForm);
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: editOpen, value: form, onClose: () => setEditOpen(false) });

  // View card dialog
  const [viewSupplier, setViewSupplier] = useState(null);
  const [vendorDocs,   setVendorDocs]   = useState(null);

  // Deactivate confirm dialog
  const [deactivateDlg, setDeactivateDlg] = useState(null);

  const { accounts: glAccounts } = useGLAccounts();
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchSuppliers = async () => {
    try {
      const [suppRes, balRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/payables/suppliers`,         { headers }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/payables/suppliers/balances`, { headers })
      ]);
      setSuppliers(suppRes.data);
      setBalances(balRes.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSuppliers(); }, []); // eslint-disable-line

  const handleCreate = () => { setEditingCode(null); setForm(emptyForm); setEditOpen(true); };
  const handleEdit   = (s)  => { setEditingCode(s.supplierCode); setForm({ ...emptyForm, ...s }); setEditOpen(true); };

  const confirmAction = async () => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/payables/suppliers/${deactivateDlg}`, { headers });
      setDeactivateDlg(null);
      fetchSuppliers();
    } catch (err) {
      setDeactivateDlg(null);
      setError(err.response?.data?.error || 'Failed to deactivate supplier');
    }
  };

  const handleSave = async () => {
    if (!editingCode && !form.supplierCode.trim()) { setError('Supplier Code is required'); return; }
    if (!form.supplierName.trim())                 { setError('Supplier Name is required'); return; }
    try {
      if (editingCode) {
        await axios.put(`${process.env.REACT_APP_API_URL}/api/payables/suppliers/${editingCode}`, form, { headers });
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/payables/suppliers`, form, { headers });
      }
      setEditOpen(false);
      setError('');
      fetchSuppliers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save supplier');
    }
  };

  // Vendor card: fetch this vendor's documents (quotes/orders/invoices/vouchers/credit notes)
  useEffect(() => {
    if (!viewSupplier) { setVendorDocs(null); return; }
    const code = viewSupplier.supplierCode;
    const base = process.env.REACT_APP_API_URL;
    Promise.allSettled([
      axios.get(`${base}/api/payables/quotes`, { headers }),
      axios.get(`${base}/api/payables/orders`, { headers }),
      axios.get(`${base}/api/payables/invoices`, { headers }),
      axios.get(`${base}/api/payables/vouchers`, { headers }),
      axios.get(`${base}/api/payables/supplier-credit-notes/${code}`, { headers }),
    ]).then(([q, o, i, v, c]) => {
      const val = (r) => (r.status === 'fulfilled' ? (r.value.data || []) : []);
      const mine = (arr) => arr.filter(d => d.supplierCode === code);
      setVendorDocs({
        quotes: mine(val(q)), orders: mine(val(o)), invoices: mine(val(i)),
        vouchers: mine(val(v)), creditNotes: val(c),
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewSupplier]);

  const balance = (code) => balances[code] || 0;

  if (loading) return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, textAlign: 'center' }}>
      <CircularProgress /><Typography sx={{ mt: 2 }}>Loading suppliers...</Typography>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              <Business sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h5" color="primary">Suppliers / Vendors</Typography>
            </Box>
            <Box>
              <IconButton onClick={() => navigate('/dashboard')} title="Dashboard"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' }, mr: 1 }}>
                <Home />
              </IconButton>
              <IconButton onClick={handleCreate} title="Add Supplier"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                <Add />
              </IconButton>
            </Box>
          </Box>

          <Toast error={error} onClose={() => setError('')} />

          {suppliers.length === 0 ? (
            <Typography>No suppliers found.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Code</strong></TableCell>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Contact</strong></TableCell>
                  <TableCell><strong>Phone</strong></TableCell>
                  <TableCell><strong>AP GL Acc.</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell align="right"><strong>Balance ({sym()})</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {suppliers.map(s => (
                  <TableRow key={s.supplierCode} hover sx={{ cursor: 'pointer' }}
                    onClick={() => setViewSupplier(s)}>
                    <TableCell><strong>{s.supplierCode}</strong></TableCell>
                    <TableCell>{s.supplierName}</TableCell>
                    <TableCell>{s.contactPerson}</TableCell>
                    <TableCell>{s.phone}</TableCell>
                    <TableCell>
                      {s.apGLAccount
                        ? <Chip label={s.apGLAccount} size="small" variant="outlined" />
                        : <Typography variant="caption" color="warning.main">Not set</Typography>}
                    </TableCell>
                    <TableCell>
                      <Chip label={s.isActive === false ? 'Inactive' : 'Active'}
                        color={s.isActive === false ? 'default' : 'success'}
                        size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="right" onClick={e => e.stopPropagation()}>
                      <Chip
                        label={`${sym()} ${fmt(balance(s.supplierCode))}`}
                        color={balance(s.supplierCode) > 0 ? 'error' : 'success'}
                        size="small" variant="outlined"
                        onClick={() => navigate(`/suppliers/${s.supplierCode}/ledger`)}
                        sx={{ cursor: 'pointer', fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      <IconButton size="small" color="primary" onClick={() => handleEdit(s)} title="Edit" sx={{ mr: 0.5 }}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="warning" onClick={() => setDeactivateDlg(s.supplierCode)}
                        title="Deactivate" disabled={s.isActive === false}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Deactivate Confirm Dialog */}
      <Dialog open={Boolean(deactivateDlg)} onClose={() => setDeactivateDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.main' }}>
          <WarningAmber color="warning" /> Deactivate Supplier
        </DialogTitle>
        <DialogContent>
          <Typography>Deactivate supplier <strong>{deactivateDlg}</strong>? They will no longer appear in new orders.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateDlg(null)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={confirmAction}>Deactivate</Button>
        </DialogActions>
      </Dialog>

      {/* ── Supplier Card (view) ── */}
      <Dialog open={Boolean(viewSupplier)} onClose={() => setViewSupplier(null)} maxWidth="sm" fullWidth>
        {viewSupplier && (
          <>
            <DialogTitle component="div">
              <Box display="flex" alignItems="center" gap={1}>
                <Business color="primary" />
                <Box>
                  <Typography variant="h6" lineHeight={1}>{viewSupplier.supplierName}</Typography>
                  <Typography variant="caption" color="text.secondary">{viewSupplier.supplierCode}</Typography>
                </Box>
                <Box ml="auto">
                  <Chip label={viewSupplier.isActive === false ? 'Inactive' : 'Active'}
                    color={viewSupplier.isActive === false ? 'default' : 'success'}
                    size="small" variant="outlined" />
                </Box>
              </Box>
            </DialogTitle>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center',
              px: 3, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
              {viewSupplier.phone && (
                <Button size="small" startIcon={<Phone />} href={`tel:${viewSupplier.phone}`}>Call</Button>
              )}
              {viewSupplier.email && (
                <Button size="small" startIcon={<Email />} href={`mailto:${viewSupplier.email}`}>Email</Button>
              )}
              <Button size="small" startIcon={<ReceiptLong />}
                onClick={() => { const c = viewSupplier.supplierCode; setViewSupplier(null); navigate(`/suppliers/${c}/ledger`); }}>Statement</Button>
              <Button size="small" startIcon={<Edit />}
                onClick={() => { setViewSupplier(null); handleEdit(viewSupplier); }}>Edit</Button>
            </Box>
            <DialogContent dividers>

              {/* General */}
              <Typography variant="subtitle2" color="primary" gutterBottom>General</Typography>
              <InfoRow label="Supplier Code"  value={viewSupplier.supplierCode} />
              <InfoRow label="Tax No (Revenue Authority No.)" value={viewSupplier.taxNo} />

              <Divider sx={{ my: 1.5 }} />

              {/* Contact */}
              <Typography variant="subtitle2" color="primary" gutterBottom>
                <Phone fontSize="inherit" sx={{ mr: 0.5, verticalAlign: 'middle' }} />Contact
              </Typography>
              <InfoRow label="Contact Person" value={viewSupplier.contactPerson} />
              <Box display="flex" gap={1} mb={0.5}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160, fontWeight: 500 }}>Phone</Typography>
                {viewSupplier.phone
                  ? <Link href={`tel:${viewSupplier.phone}`} variant="body2" underline="hover">{viewSupplier.phone}</Link>
                  : <Typography variant="body2"><em style={{ color: '#bbb' }}>—</em></Typography>}
              </Box>
              <Box display="flex" gap={1} mb={0.5}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160, fontWeight: 500 }}>Email</Typography>
                {viewSupplier.email
                  ? <Link href={`mailto:${viewSupplier.email}`} variant="body2" underline="hover">{viewSupplier.email}</Link>
                  : <Typography variant="body2"><em style={{ color: '#bbb' }}>—</em></Typography>}
              </Box>
              <InfoRow label="Address"        value={viewSupplier.address} />

              <Divider sx={{ my: 1.5 }} />

              {/* Banking */}
              <Typography variant="subtitle2" color="primary" gutterBottom>
                <AccountBalance fontSize="inherit" sx={{ mr: 0.5, verticalAlign: 'middle' }} />Banking
              </Typography>
              <InfoRow label="Bank Name"         value={viewSupplier.bankName} />
              <InfoRow label="Bank Account No"   value={viewSupplier.bankAccountNo} />
              <InfoRow label="Bank Account Name" value={viewSupplier.bankAccountName} />

              <Divider sx={{ my: 1.5 }} />

              {/* Accounting */}
              <Typography variant="subtitle2" color="primary" gutterBottom>
                <Receipt fontSize="inherit" sx={{ mr: 0.5, verticalAlign: 'middle' }} />Accounting
              </Typography>
              <InfoRow label="AP GL Account" value={viewSupplier.apGLAccount} />
              <Box display="flex" alignItems="center" gap={1} mt={1}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140, fontWeight: 500 }}>
                  Outstanding Balance
                </Typography>
                <Chip
                  label={`${sym()} ${fmt(balance(viewSupplier.supplierCode))}`}
                  color={balance(viewSupplier.supplierCode) > 0 ? 'error' : 'success'}
                  size="small" variant="outlined"
                  onClick={() => { setViewSupplier(null); navigate(`/suppliers/${viewSupplier.supplierCode}/ledger`); }}
                  sx={{ cursor: 'pointer', fontWeight: 600 }}
                />
                <Typography variant="caption" color="text.secondary">(click to view ledger)</Typography>
              </Box>

              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle2" color="primary" gutterBottom>
                <ShoppingCart fontSize="inherit" sx={{ mr: 0.5, verticalAlign: 'middle' }} />New Purchase Document
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap" mb={0.5}>
                <Button size="small" variant="outlined" startIcon={<RequestQuote />}
                  onClick={() => { const c = viewSupplier.supplierCode; setViewSupplier(null); navigate(`/payables/quotes/new?supplier=${c}`); }}>Quote</Button>
                <Button size="small" variant="outlined" startIcon={<ShoppingCart />}
                  onClick={() => { const c = viewSupplier.supplierCode; setViewSupplier(null); navigate(`/payables/orders/new?supplier=${c}`); }}>Order</Button>
                <Button size="small" variant="outlined" startIcon={<Description />}
                  onClick={() => { const c = viewSupplier.supplierCode; setViewSupplier(null); navigate(`/payables/invoices/new?supplier=${c}`); }}>Invoice</Button>
              </Box>

              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle2" color="primary" gutterBottom>
                <ReceiptLong fontSize="inherit" sx={{ mr: 0.5, verticalAlign: 'middle' }} />Recent Documents
              </Typography>
              {!vendorDocs ? (
                <Typography variant="caption" color="text.secondary">Loading…</Typography>
              ) : (() => {
                const recent = [
                  ...vendorDocs.quotes.map(d => ({ type: 'Quote', no: d.quoteNo, date: d.quoteDate, amount: d.totalAmount, status: d.status, path: '/payables/quotes' })),
                  ...vendorDocs.orders.map(d => ({ type: 'Order', no: d.orderNo, date: d.orderDate, amount: d.totalAmount, status: d.status, path: '/payables/orders' })),
                  ...vendorDocs.invoices.map(d => ({ type: 'Invoice', no: d.invoiceNo, date: d.invoiceDate, amount: d.totalAmount, status: d.status, path: '/payables/invoices' })),
                  ...vendorDocs.vouchers.map(d => ({ type: 'Payment', no: d.voucherNo, date: d.voucherDate, amount: d.amount, status: d.status, path: '/payables/vouchers' })),
                  ...vendorDocs.creditNotes.map(d => ({ type: 'Credit', no: d.creditNoteNo, date: d.date, amount: d.amount, status: d.status, path: '/payables/credit-notes' })),
                ].sort((a, b) => new Date(b.date) - new Date(a.date));
                if (recent.length === 0) return <Typography variant="caption" color="text.secondary">No documents yet for this vendor.</Typography>;
                return (
                  <>
                    <Box display="flex" gap={0.5} flexWrap="wrap" mb={1}>
                      <Chip size="small" label={`Quotes ${vendorDocs.quotes.length}`} variant="outlined" />
                      <Chip size="small" label={`Orders ${vendorDocs.orders.length}`} variant="outlined" />
                      <Chip size="small" label={`Invoices ${vendorDocs.invoices.length}`} variant="outlined" />
                      <Chip size="small" label={`Payments ${vendorDocs.vouchers.length}`} variant="outlined" />
                      <Chip size="small" label={`Credits ${vendorDocs.creditNotes.length}`} variant="outlined" />
                    </Box>
                    <Table size="small">
                      <TableBody>
                        {recent.slice(0, 6).map((d, idx) => (
                          <TableRow key={idx} hover sx={{ cursor: 'pointer' }}
                            onClick={() => { setViewSupplier(null); navigate(d.path); }}>
                            <TableCell sx={{ py: 0.4 }}><Chip size="small" label={d.type} /></TableCell>
                            <TableCell sx={{ py: 0.4 }}>{d.no}</TableCell>
                            <TableCell sx={{ py: 0.4 }}>{d.date ? new Date(d.date).toLocaleDateString() : '—'}</TableCell>
                            <TableCell align="right" sx={{ py: 0.4 }}>{sym()} {fmt(d.amount || 0)}</TableCell>
                            <TableCell sx={{ py: 0.4 }}>{d.status ? <Chip size="small" variant="outlined" label={d.status} /> : ''}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                );
              })()}
            </DialogContent>
            <DialogActions>
              <Button variant="outlined" onClick={() => setViewSupplier(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── Edit / Create dialog ── */}
      {discardDialog}
      <Dialog open={editOpen} onClose={attemptClose} fullWidth maxWidth="sm">
        <DialogTitle>{editingCode ? `Edit Supplier: ${editingCode}` : 'New Supplier'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField label="Supplier Code" value={form.supplierCode}
              onChange={e => setForm({ ...form, supplierCode: e.target.value })}
              disabled={!!editingCode} fullWidth size="small" />
            <TextField label="Supplier Name" value={form.supplierName}
              onChange={e => setForm({ ...form, supplierName: e.target.value })}
              fullWidth size="small" />

            <Divider><Typography variant="caption">Contact</Typography></Divider>
            <TextField label="Contact Person" value={form.contactPerson}
              onChange={e => setForm({ ...form, contactPerson: e.target.value })} fullWidth size="small" />
            <TextField label="Phone" value={form.phone}
              onChange={e => setForm({ ...form, phone: e.target.value })} fullWidth size="small" />
            <TextField label="Email" type="email" value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })} fullWidth size="small" />
            <TextField label="Address" value={form.address}
              onChange={e => setForm({ ...form, address: e.target.value })} fullWidth size="small" />
            <TextField label="Tax No (Revenue Authority No.)" value={form.taxNo}
              onChange={e => setForm({ ...form, taxNo: e.target.value })} fullWidth size="small" />

            <Divider><Typography variant="caption">Banking</Typography></Divider>
            <TextField label="Bank Name" value={form.bankName}
              onChange={e => setForm({ ...form, bankName: e.target.value })} fullWidth size="small" />
            <TextField label="Bank Account No" value={form.bankAccountNo}
              onChange={e => setForm({ ...form, bankAccountNo: e.target.value })} fullWidth size="small" />
            <TextField label="Bank Account Name" value={form.bankAccountName}
              onChange={e => setForm({ ...form, bankAccountName: e.target.value })} fullWidth size="small" />

            <Divider><Typography variant="caption">Accounting</Typography></Divider>
            <TextField select label="AP GL Account (Creditors / Payables)" value={form.apGLAccount}
              onChange={e => setForm({ ...form, apGLAccount: e.target.value })}
              fullWidth size="small" helperText="Required to post purchase orders">
              {glAccountOptions(glAccounts, { noneLabel: 'Select GL Account' })}
            </TextField>
            {editingCode && (
              <FormControlLabel
                control={<Switch checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} />}
                label="Active" />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupplierList;
